import React, { useEffect } from 'react';
import { Field, useForm } from 'react-final-form';
import PropTypes from 'prop-types';

import FieldLabelAndError from './FieldLabelAndError';
import {
    requiredUtil,
    validateAndRequiredEmailUtil,
    validateEmailUtil,
    validateNumberUtil,
    validateAndRequiredNumberUtil,
    validateDateUtil,
    validateAndRequiredDateUtil,
    validateAndRequiredAlphaNumericTextUtil,
} from '../utils';
import { ErrorMessage } from './ErrorMessage';
import { BorderStyle } from './FormFieldsStyling';

function selectValidation(props) {
    if (props.validateAndRequiredEmail) {
        return validateAndRequiredEmailUtil;
    }
    if (props.validateEmail) {
        return validateEmailUtil;
    }
    if (props.validateAndRequiredNumber) {
        return validateAndRequiredNumberUtil;
    }
    if (props.validateNumber) {
        return validateNumberUtil;
    }
    if (props.validateDate) {
        return validateDateUtil;
    }
    if (props.validateAndRequiredDate) {
        return validateAndRequiredDateUtil;
    }
    if (props.validateAndRequiredAlphaNumericText) {
        return validateAndRequiredAlphaNumericTextUtil;
    }
    if (props.validateAndRequiredZipCodeUtil) {
        return props.validateAndRequiredZipCodeUtil;
    }
    // Validation util method passed in props
    if (props.validateUtil) {
        return props.validateUtil;
    }
    if (props.required) {
        return requiredUtil;
    }
    return null;
}

const GenericTextInputField = ({
    name,
    label,
    type,
    placeholder,
    className,
    disabled,
    required,
    onFocus,
    onBlur,
    onPaste,
    validateEmail,
    validateAndRequiredEmail,
    validateNumber,
    validateAndRequiredNumber,
    validateDate,
    validateAndRequiredDate,
    validateAndRequiredZipCodeUtil,
    validateAndRequiredAlphaNumericText,
    validateUtil,
    clearOnUnmount,
    dataTestId,
    id,
}) => {
    const form = useForm();
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (clearOnUnmount) {
            return () => form.change(name);
        }
    }, [clearOnUnmount]);

    let inputcss =
        className ||
        `md:mt-1 w-full h-8 pl-2 ml-1 block placeholder-gray-500 shadow-sm sm:text-sm rounded-md ${BorderStyle}`;

    if (disabled) inputcss += ' bg-gray-400';

    return (
        <Field
            name={name}
            validate={selectValidation({
                required,
                validateEmail,
                validateAndRequiredEmail,
                validateNumber,
                validateAndRequiredNumber,
                validateDate,
                validateAndRequiredDate,
                validateAndRequiredAlphaNumericText,
                validateAndRequiredZipCodeUtil,
                validateUtil,
            })}
        >
            {({ input, meta }) => (
                <div className="mb-4">
                    <FieldLabelAndError
                        label={label}
                        meta={meta}
                        required={
                            required ||
                            validateAndRequiredNumber ||
                            validateAndRequiredEmail ||
                            validateAndRequiredDate ||
                            validateAndRequiredAlphaNumericText ||
                            validateAndRequiredZipCodeUtil ||
                            validateUtil
                        }
                    />
                    <div className="w-full">
                        <input
                            {...input}
                            className={inputcss}
                            type={type}
                            placeholder={placeholder}
                            disabled={disabled}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onPaste={onPaste}
                            data-testid={dataTestId || name}
                            id={id}
                        />
                        <ErrorMessage meta={meta} />
                    </div>
                </div>
            )}
        </Field>
    );
};

GenericTextInputField.propTypes = {
    name: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    placeholder: PropTypes.string,
    type: PropTypes.string,
    className: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onPaste: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    validateEmail: PropTypes.bool,
    validateAndRequiredEmail: PropTypes.bool,
    validateNumber: PropTypes.bool,
    validateAndRequiredNumber: PropTypes.bool,
    validateDate: PropTypes.bool,
    validateAndRequiredDate: PropTypes.bool,
    validateAndRequiredAlphaNumericText: PropTypes.bool,
    validateAndRequiredZipCodeUtil: PropTypes.func,
    validateUtil: PropTypes.func,
    clearOnUnmount: PropTypes.bool,
    dataTestId: PropTypes.string,
    id: PropTypes.string,
};

export default GenericTextInputField;
